import { create } from "zustand";
import { useMediaQuery, useTheme } from "@mui/material";

interface DrawerState {
  isOpen: boolean;
  show: () => void;
  hide: () => void;
  toggle: () => void;
}

// Desktop drawer state with localStorage persistence
const useDesktopDrawerStore = create<DrawerState>((set) => {
  // Load initial state from localStorage
  const stored = localStorage.getItem("drawer-desktop-open");
  const initialOpen = stored ? JSON.parse(stored) : true;

  return {
    isOpen: initialOpen,
    show: () => {
      set({ isOpen: true });
      localStorage.setItem("drawer-desktop-open", "true");
    },
    hide: () => {
      set({ isOpen: false });
      localStorage.setItem("drawer-desktop-open", "false");
    },
    toggle: () => {
      set((state) => {
        const newState = !state.isOpen;
        localStorage.setItem("drawer-desktop-open", JSON.stringify(newState));
        return { isOpen: newState };
      });
    },
  };
});

// Mobile drawer state (no persistence)
const useMobileDrawerStore = create<DrawerState>((set) => ({
  isOpen: false,
  show: () => set({ isOpen: true }),
  hide: () => set({ isOpen: false }),
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
}));

/**
 * Hook to manage drawer state (mobile or desktop).
 *
 * Automatically returns the appropriate drawer state based on screen size.
 * Desktop state persists to localStorage, mobile state is ephemeral.
 */
export function useDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const desktopDrawer = useDesktopDrawerStore();
  const mobileDrawer = useMobileDrawerStore();

  return isMobile ? mobileDrawer : desktopDrawer;
}
